import { SupportingDocumentsList } from "@/components/review/SupportingDocumentsList";
import { useReviewLabels } from "@/hooks/useReviewLabels";
import { BookOpen, FileText, Globe, Info, MessageSquare, User } from "lucide-react";

export type ReviewConfigurationFields = {
  reviewId?: string | null;
  contractType?: string | null;
  jurisdiction?: string | null;
  userRole?: string | null;
  skipResearch?: boolean | null;
  backgroundInfo?: string | null;
  agentInstructions?: string | null;
};

export function hasReviewConfiguration(fields: ReviewConfigurationFields): boolean {
  return Boolean(
    fields.contractType ||
      fields.jurisdiction ||
      fields.userRole ||
      fields.backgroundInfo ||
      fields.agentInstructions ||
      fields.skipResearch === true ||
      fields.skipResearch === false
  );
}

export function ReviewConfigurationCard({
  reviewId,
  contractType,
  jurisdiction,
  userRole,
  skipResearch,
  backgroundInfo,
  agentInstructions,
  className,
}: ReviewConfigurationFields & { className?: string }) {
  const { getContractTypeLabel, getRoleLabel } = useReviewLabels();

  if (
    !hasReviewConfiguration({
      contractType,
      jurisdiction,
      userRole,
      skipResearch,
      backgroundInfo,
      agentInstructions,
    })
  ) {
    return null;
  }

  return (
    <div
      className={
        className ??
        "rounded-xl border border-[var(--review-config-border)] bg-[var(--review-config-bg)] p-4 dark:border-slate-700/50 dark:bg-slate-900/30 dark:backdrop-blur-sm"
      }
    >
      <div className="mb-3 flex items-center gap-2">
        <Info className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-200">
          Review Configuration
        </h3>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="flex items-start gap-2">
          <FileText className="mt-0.5 h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" />
          <div>
            <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">Contract Type</p>
            <p className="text-sm text-[var(--lm-foreground-strong)] dark:text-slate-200">
              {getContractTypeLabel(contractType)}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Globe className="mt-0.5 h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" />
          <div>
            <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">Governing Law</p>
            <p className="text-sm text-[var(--lm-foreground-strong)] dark:text-slate-200">
              {jurisdiction || "Not specified"}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <User className="mt-0.5 h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" />
          <div>
            <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">Review Role</p>
            <p className="text-sm text-[var(--lm-foreground-strong)] dark:text-slate-200">
              {getRoleLabel(userRole)}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 border-t border-[var(--review-config-divider)] pt-4 dark:border-slate-700/30 md:grid-cols-2">
        <div className="flex items-start gap-2">
          <BookOpen className="mt-0.5 h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" />
          <div>
            <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">Legal Research</p>
            <p className="text-sm text-[var(--lm-foreground-strong)] dark:text-slate-200">
              {skipResearch ? "Skipped (Faster)" : "Enabled (Full Analysis)"}
            </p>
          </div>
        </div>

        {agentInstructions ? (
          <div className="flex items-start gap-2">
            <MessageSquare className="mt-0.5 h-4 w-4 text-[var(--lm-muted)] dark:text-slate-400" />
            <div className="flex-1">
              <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">Special Instructions</p>
              <p className="line-clamp-2 text-sm text-[var(--lm-foreground-strong)] dark:text-slate-200">
                {agentInstructions}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {backgroundInfo ? (
        <div className="mt-4 border-t border-[var(--review-config-divider)] pt-4 dark:border-slate-700/30">
          <p className="mb-1 text-xs text-[var(--lm-muted)] dark:text-slate-500">Background Information</p>
          <p className="text-sm text-[var(--lm-foreground)] dark:text-slate-300">{backgroundInfo}</p>
        </div>
      ) : null}

      {reviewId ? (
        <SupportingDocumentsList
          reviewId={reviewId}
          className="mt-4 border-t border-[var(--review-config-divider)] pt-4 dark:border-slate-700/30"
        />
      ) : null}
    </div>
  );
}
